// @file Composable that provides methods to manage the state of post composer modals.
import { isAppUsing } from '@@/bits/flip'
import { unboundedWatch } from '@@/bits/vue'
import { useComposerModalStore } from '@@/pinia/composer_modal'
import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { computed, readonly, ref, shallowRef, watch } from 'vue'

// `import type DockableModalList ...` doesn't work in TypeScript files so we just
// declare the types of these `DockableModalList` exposed values here.
interface DockableModalListInstance {
  minimizeModal: (id: string) => void
  minimizeAllModals: () => void
  dockModal: (id: string) => void
  expandModal: (id: string) => void
  restoreModalState: (id: string) => void
  closeModal: (id: string) => void
  hasDockedModal: boolean
  hasExpandedModal: boolean
}

export const usePostComposerModalStore = defineStore('postComposerModalStore', () => {
  const dockableModalList = shallowRef<DockableModalListInstance>()
  const composerModalStore = useComposerModalStore()

  const hasDockedComposerModal = computed<boolean>(() => {
    if (!isAppUsing('postComposerV2Animation')) {
      return composerModalStore.isDocked
    }
    return dockableModalList.value?.hasDockedModal ?? false
  })

  const hasExpandedComposerModal = computed<boolean>(() => {
    if (!isAppUsing('postComposerV2Animation')) {
      return composerModalStore.isExpanded
    }
    return dockableModalList.value?.hasExpandedModal ?? false
  })

  const registerDockableModalListInstance = (instance: Ref<DockableModalListInstance | undefined>): void => {
    const stop = watch(instance, (inst) => {
      if (inst === undefined) return
      dockableModalList.value = inst
      stop()
    })
  }

  const throwErrorIfNotExposed = (): void => {
    if (dockableModalList.value === undefined) {
      throw new Error(
        'No instance of `DockableModalList` found. Ensure `DockableModalList` is mounted and `registerDockableModalListInstance` has been called.',
      )
    }
  }

  const isSubmissionRequestComposerModalExpanded = ref(true)
  const isShowingAttachmentPreview = ref(false)

  const syncIsShowingAttachmentPreview = (isShowing: Ref<boolean>): void => {
    void unboundedWatch(
      isShowing,
      (value) => {
        isShowingAttachmentPreview.value = value
      },
      { immediate: true },
    )
  }

  return {
    registerDockableModalListInstance,

    // Surface Composer Modals
    minimizeComposerModal: (id: string) => {
      throwErrorIfNotExposed()
      dockableModalList.value?.minimizeModal(id)
    },
    minimizeAllComposerModals: () => {
      throwErrorIfNotExposed()
      dockableModalList.value?.minimizeAllModals()
    },
    dockComposerModal: (id: string) => {
      if (!isAppUsing('postComposerV2Animation')) {
        composerModalStore.dockComposerModal()
        return
      }
      throwErrorIfNotExposed()
      dockableModalList.value?.dockModal(id)
    },
    expandComposerModal: (id: string) => {
      if (!isAppUsing('postComposerV2Animation')) {
        composerModalStore.expandComposerModal()
        return
      }
      throwErrorIfNotExposed()
      dockableModalList.value?.expandModal(id)
    },
    restoreComposerModalState: (id: string) => {
      throwErrorIfNotExposed()
      dockableModalList.value?.restoreModalState(id)
    },
    closeComposerModal: (id: string) => {
      throwErrorIfNotExposed()
      dockableModalList.value?.closeModal(id)
    },
    hasDockedComposerModal,
    hasExpandedComposerModal,

    // Submission Request Composer Modal
    expandSubmissionRequestComposerModal: () => {
      if (!isAppUsing('postComposerV2Animation')) {
        composerModalStore.expandMobile()
        return
      }
      isSubmissionRequestComposerModalExpanded.value = true
    },
    collapseSubmissionRequestComposerModal: () => {
      if (!isAppUsing('postComposerV2Animation')) {
        composerModalStore.collapseMobile()
        return
      }
      isSubmissionRequestComposerModalExpanded.value = false
    },
    isSubmissionRequestComposerModalExpanded: computed(() => {
      if (!isAppUsing('postComposerV2Animation')) {
        return composerModalStore.isExpandedMobile
      }
      return isSubmissionRequestComposerModalExpanded.value
    }),

    isShowingAttachmentPreview: readonly(isShowingAttachmentPreview),
    syncIsShowingAttachmentPreview,
  }
})
